import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useParams } from "react-router";
import { isChief, type ActorDTO, type MessageDTO, type TaskDTO, type ThreadDTO } from "@roomate/core";
import { useActors, useAskChief, useChief, useChiefPausedReason, useMe, useMessages, useProjects, useReadMessages, useSendMessage, useTasks, useThreads } from "../api.ts";
import { chiefActor, chiefBanner, chiefLabel, citationsOf, isChiefThread, NEW_CHIEF_THREAD, pickChiefThread, resolveSelected } from "../lib/chiefThread.ts";
import { useCurrentProject } from "../project.tsx";
import { historyName, mentionPeople } from "../roster.ts";
import { Action, Avatar, Bento, Page, PageHeader, Pill, Tile } from "../calm.tsx";
import { cx, ErrorNote, formatAgo, inputCls, Markdown } from "../ui.tsx";

type Filters = { agent: string; person: string; project: string; task: string };
const EMPTY_FILTERS: Filters = { agent: "", person: "", project: "", task: "" };

/** People only see threads they participate in; server-side actor scoping also makes unread counts personal. */
export function ConversationsPage() {
  const me = useMe().data!;
  const threads = useThreads({ actorId: "me" });
  const actors = useActors().data ?? [];
  const projects = useProjects().data ?? [];
  const tasks = useTasks().data ?? [];
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const chiefRead = useChief().data ?? null;
  const { projectId: currentProjectId } = useCurrentProject();
  const chief = me.kind === "human" ? chiefActor(actors) : null;
  const chiefInfo: ChiefInfo | null = chief ? { actor: chief, state: chiefRead?.state, reason: chiefRead?.reason ?? null } : null;
  // /conversations/:threadId opens that thread (Roomie's Chief hand-off lands here).
  const { threadId: linkedThread } = useParams();
  const [selectedId, setSelectedId] = useState<string | null>(linkedThread ?? null);
  const { key: navKey } = useLocation();
  // Every arrival counts (navKey), so asking the Chief again re-selects its thread, and a filter cannot hide it.
  useEffect(() => {
    if (!linkedThread) return;
    setSelectedId(linkedThread);
    setFilters(EMPTY_FILTERS);
  }, [linkedThread, navKey]);

  // The Chief thread is pinned and ignores the filters; before the first message a placeholder stands in for it.
  // Scoped to the current project, like Roomie's hand-off. Chief threads of other projects stay in the list and use the Chief composer too.
  const loaded = threads.data !== undefined;
  const chiefThread = useMemo(() => (chief ? pickChiefThread(threads.data ?? [], currentProjectId, me.id, chief.id) : null), [chief, currentProjectId, me.id, threads.data]);
  const pinned: ThreadDTO | null = chief && loaded
    ? (chiefThread ?? { threadId: NEW_CHIEF_THREAD, projectId: currentProjectId, taskId: null, participants: [me.id, chief.id], lastMessageAt: 0, messageCount: 0, unreadCount: 0 })
    : null;
  const visible = useMemo(
    () =>
      (threads.data ?? []).filter(
        (thread) =>
          thread.threadId !== chiefThread?.threadId &&
          (!filters.agent || thread.participants.includes(filters.agent)) &&
          (!filters.person || thread.participants.includes(filters.person)) &&
          (!filters.project || thread.projectId === filters.project) &&
          (!filters.task || thread.taskId === filters.task),
      ),
    [filters, threads.data, chiefThread],
  );
  // The placeholder id stands for whatever the pinned thread is now, so a thread that appears under it keeps the selection.
  const selected = resolveSelected(selectedId, loaded, pinned, visible);

  useEffect(() => {
    // A deep-linked thread that the list has not caught up with yet keeps its place instead of falling back to the newest.
    if (linkedThread && selectedId === linkedThread && selected?.threadId !== linkedThread) return;
    if (selected && selected.threadId !== selectedId) setSelectedId(selected.threadId);
    if (!selected && selectedId) setSelectedId(null);
  }, [selected, selectedId]);

  const grouped = useMemo(() => groupThreads(visible), [visible]);
  const unread = (threads.data ?? []).reduce((sum, thread) => sum + thread.unreadCount, 0);

  return (
    <Page wide>
      <PageHeader
        title="Conversations"
        meta={
          threads.isLoading ? (
            <span className="text-ink-3">Loading…</span>
          ) : (
            <span className="text-ink-3">
              {threads.data?.length ?? 0} thread{threads.data?.length === 1 ? "" : "s"}{unread ? ` · ${unread} unread` : " · all caught up"}
            </span>
          )
        }
      />

      <Bento className="mt-8">
        <Tile span={6} title="Filter" aside={visible.length === (threads.data?.length ?? 0) ? undefined : `${visible.length} shown`}>
          <ThreadFilters filters={filters} onChange={setFilters} actors={actors} projects={projects} tasks={tasks} />
        </Tile>

        <Tile span={2} title="Threads" aside={visible.length} testId="conversation-thread-list">
          {threads.error ? (
            <ErrorNote error={threads.error} />
          ) : grouped.length === 0 && !pinned ? (
            <p className="text-[13.5px] text-ink-3">{threads.isLoading ? "Loading…" : "No conversations match these filters."}</p>
          ) : (
            <div className="max-h-[64vh] space-y-5 overflow-y-auto pr-1">
              {pinned && chief && (
                <section data-testid="conversation-chief-pinned">
                  <h2 className="mb-1.5 text-[12px] font-medium text-ink-3">Pinned</h2>
                  <ThreadButton thread={pinned} actors={actors} meId={me.id} active={pinned.threadId === selected?.threadId} onOpen={() => setSelectedId(pinned.threadId)} title={chiefLabel(chief)} />
                </section>
              )}
              {grouped.map((group) => {
                const task = group.taskId ? tasks.find((item) => item.id === group.taskId) : undefined;
                return (
                  <section key={group.taskId ?? "none"} data-testid="conversation-task-group">
                    <h2 className="mb-1.5 truncate text-[12px] font-medium text-ink-3">
                      {task ? `#${task.number} · ${task.title}` : "No task"}
                    </h2>
                    <ol className="space-y-1">
                      {group.threads.map((thread) => (
                        <li key={thread.threadId}>
                          <ThreadButton thread={thread} actors={actors} meId={me.id} active={thread.threadId === selected?.threadId} onOpen={() => setSelectedId(thread.threadId)} />
                        </li>
                      ))}
                    </ol>
                  </section>
                );
              })}
            </div>
          )}
        </Tile>

        <Tile span={4} testId="conversation-panel">
          {selected ? <Conversation key={selected.threadId} thread={selected} actors={actors} tasks={tasks} me={me} chief={chiefInfo && (selected.threadId === pinned?.threadId || isChiefThread(selected, me.id, chiefInfo.actor.id)) ? chiefInfo : null} projectId={currentProjectId} onChiefSent={setSelectedId} /> : <p className="text-[13.5px] text-ink-3">{threads.isLoading ? "Loading…" : "Choose a thread to read it."}</p>}
        </Tile>
      </Bento>
    </Page>
  );
}

function ThreadFilters({
  filters,
  onChange,
  actors,
  projects,
  tasks,
}: {
  filters: Filters;
  onChange: (filters: Filters) => void;
  actors: ActorDTO[];
  projects: { id: string; name: string }[];
  tasks: TaskDTO[];
}) {
  const set = (key: keyof Filters, value: string) => onChange({ ...filters, [key]: value, ...(key === "project" && filters.task && !tasks.some((task) => task.id === filters.task && (!value || task.projectId === value)) ? { task: "" } : {}) });
  const taskOptions = tasks.filter((task) => !filters.project || task.projectId === filters.project).sort((a, b) => a.number - b.number);
  const active = Object.values(filters).some(Boolean);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select value={filters.agent} onChange={(event) => set("agent", event.target.value)} className={cx(inputCls, "w-auto min-w-36 rounded-full")} aria-label="Filter by agent" data-testid="conversation-filter-agent">
        <option value="">All agents</option>
        {actors.filter((actor) => actor.kind === "agent").map((actor) => <option key={actor.id} value={actor.id}>{actor.name}</option>)}
      </select>
      <select value={filters.person} onChange={(event) => set("person", event.target.value)} className={cx(inputCls, "w-auto min-w-36 rounded-full")} aria-label="Filter by person" data-testid="conversation-filter-person">
        <option value="">All people</option>
        {mentionPeople(actors).map((actor) => <option key={actor.id} value={actor.id}>{actor.name}</option>)}
      </select>
      <select value={filters.project} onChange={(event) => set("project", event.target.value)} className={cx(inputCls, "w-auto min-w-36 rounded-full")} aria-label="Filter by project" data-testid="conversation-filter-project">
        <option value="">All projects</option>
        {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
      </select>
      <select value={filters.task} onChange={(event) => set("task", event.target.value)} className={cx(inputCls, "w-auto min-w-44 rounded-full")} aria-label="Filter by task" data-testid="conversation-filter-task">
        <option value="">All tasks</option>
        {taskOptions.map((task) => <option key={task.id} value={task.id}>#{task.number} · {task.title}</option>)}
      </select>
      {active && <Action onClick={() => onChange(EMPTY_FILTERS)} testId="conversation-filters-clear">Clear</Action>}
    </div>
  );
}

function ThreadButton({ thread, actors, meId, active, onOpen, title }: { thread: ThreadDTO; actors: ActorDTO[]; meId: string; active: boolean; onOpen: () => void; title?: string }) {
  const others = thread.participants.filter((id) => id !== meId).map((id) => historyName(actors, id));
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cx("flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition-colors", active ? "bg-ink/[0.09]" : "hover:bg-ink/[0.05]")}
      data-testid={title ? "conversation-chief-thread" : "conversation-thread"}
      aria-pressed={active}
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13.5px] font-medium">{title ?? (others.join(", ") || "Conversation")}</span>
        <span className="block text-[11.5px] text-ink-3">
          {thread.messageCount === 0 ? "No messages yet" : `${thread.messageCount} message${thread.messageCount === 1 ? "" : "s"} · ${formatAgo(Date.now() - thread.lastMessageAt)} ago`}
        </span>
      </span>
      {thread.unreadCount > 0 && <Pill tone="signal" testId="conversation-thread-unread">{thread.unreadCount}</Pill>}
    </button>
  );
}

type ChiefInfo = { actor: ActorDTO; state: string | undefined; reason: string | null };

function Conversation({
  thread,
  actors,
  tasks,
  me,
  chief,
  projectId,
  onChiefSent,
}: {
  thread: ThreadDTO;
  actors: ActorDTO[];
  tasks: TaskDTO[];
  me: ActorDTO;
  /** Set for every Chief thread (pinned or not), so each wakes the Chief and shows its state. */
  chief: ChiefInfo | null;
  projectId: string;
  onChiefSent: (threadId: string) => void;
}) {
  const isNew = thread.threadId === NEW_CHIEF_THREAD;
  const messages = useMessages({ threadId: isNew ? "" : thread.threadId });
  const pausedReason = useChiefPausedReason(chief?.state === "paused" && !chief.reason).data ?? null;
  const banner = chief ? chiefBanner(chief.state, chief.reason ?? pausedReason) : null;
  const markRead = useReadMessages();
  const attemptedReadIds = useRef(new Set<string>());
  const task = thread.taskId ? tasks.find((item) => item.id === thread.taskId) : undefined;
  const unreadIds = (messages.data ?? []).filter((message) => message.toActor === me.id && message.readAt === null).map((message) => message.id);

  useEffect(() => {
    if (me.access === "viewer" || markRead.isPending) return;
    const unattempted = unreadIds.filter((id) => !attemptedReadIds.current.has(id));
    if (!unattempted.length) return;
    for (const id of unattempted) attemptedReadIds.current.add(id);
    markRead.mutate(
      { ids: unattempted, threadId: thread.threadId },
      // A failed attempt must not be remembered as "attempted" forever, or a transient network
      // error permanently stops retrying — the invalidated refetch brings readAt back to null,
      // but without this the ref would keep excluding these ids from every future unattempted check.
      { onError: () => { for (const id of unattempted) attemptedReadIds.current.delete(id); } },
    );
  }, [markRead.isPending, markRead.mutate, me.access, thread.threadId, unreadIds.join("|")]);

  return (
    <div className="flex min-h-[54vh] flex-col">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-rule/60 pb-4">
        <div>
          <h2 className="text-[18px] font-semibold" data-testid={chief ? "chief-thread-title" : undefined}>{chief ? chiefLabel(chief.actor) : participantNames(thread, actors).join(", ")}</h2>
          <p className="mt-0.5 text-[12px] text-ink-3">{thread.messageCount} message{thread.messageCount === 1 ? "" : "s"}</p>
        </div>
        {chief && <Link to="/chief" className="text-[12.5px] text-ink-2 underline decoration-rule hover:text-ink" data-testid="chief-thread-actions-link">Actions by the Chief</Link>}
        {task && <Link to={`/tasks/${task.id}`} className="text-[12.5px] text-ink-2 underline decoration-rule hover:text-ink">#{task.number} · {task.title}</Link>}
      </div>
      {banner && (
        <p role="status" data-testid="chief-state-banner" data-state={banner.state} className="mt-4 rounded-2xl bg-signal-soft px-4 py-3 text-[13px] text-ink-2">
          {banner.text}
        </p>
      )}
      <ol className="flex-1 space-y-3 overflow-y-auto py-5" data-testid="conversation-messages" aria-live="polite">
        {[...(messages.data ?? [])].reverse().map((message) => <MessageBubble key={message.id} message={message} actors={actors} tasks={tasks} mine={message.fromActor === me.id} />)}
        {isNew && <li className="text-[13px] text-ink-3" data-testid="chief-thread-empty">Ask the Chief anything about the office. It answers here and cites what it read.</li>}
      </ol>
      {messages.error && <ErrorNote error={messages.error} />}
      {markRead.error && <ErrorNote error={markRead.error} />}
      {me.access === "viewer" ? (
        <p className="border-t border-rule/60 pt-4 text-[12.5px] text-ink-3">Viewers cannot post messages.</p>
      ) : chief ? (
        <ChiefComposer chief={chief.actor} threadId={isNew ? null : thread.threadId} projectId={isNew ? projectId : thread.projectId} disabled={(banner ? !banner.canPost : false)} onSent={onChiefSent} />
      ) : (
        <Composer threadId={thread.threadId} />
      )}
    </div>
  );
}

function MessageBubble({ message, actors, tasks, mine }: { message: MessageDTO; actors: ActorDTO[]; tasks: TaskDTO[]; mine: boolean }) {
  const actor = actors.find((item) => item.id === message.fromActor);
  const fromChief = !!actor && isChief(actor);
  const citations = fromChief ? citationsOf(message, tasks) : [];
  return (
    <li className={cx("flex gap-2.5", mine && "flex-row-reverse")} data-testid="conversation-message">
      <Avatar name={actor?.name ?? "?"} kind={actor?.kind ?? "agent"} size={28} />
      <div className={cx("max-w-[82%] rounded-2xl px-4 py-3", mine ? "bg-ink text-paper" : "bg-ink/[0.05]")}>
        <div className={cx("mb-1 flex gap-2 text-[11px]", mine ? "text-paper/60" : "text-ink-3")}>
          <span>{fromChief ? chiefLabel(actor) : historyName(actors, message.fromActor) || "Unknown"}</span><span>{formatAgo(Date.now() - message.createdAt)} ago</span>
        </div>
        <div className={cx("text-[13.5px] leading-relaxed", mine && "[&_a]:text-paper")}><Markdown text={message.body} /></div>
        {citations.length > 0 && (
          <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-ink-3" data-testid="chief-citations">
            <span>Read:</span>
            {citations.map((c) => (
              <Link key={c.key} to={c.to} className="text-ink-2 underline decoration-rule hover:text-ink" data-testid="chief-citation">{c.label}</Link>
            ))}
          </p>
        )}
        {mine && <p className="mt-1 text-right text-[10.5px] text-paper/55">{message.readAt ? "read" : message.deliveredAt ? "delivered" : "sent"}</p>}
      </div>
    </li>
  );
}

function Composer({ threadId }: { threadId: string }) {
  const send = useSendMessage();
  const [body, setBody] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const text = body.trim();
    if (!text) return;
    send.mutate({ replyTo: threadId, body: text }, { onSuccess: () => setBody("") });
  };
  return (
    <form onSubmit={submit} className="border-t border-rule/60 pt-4" data-testid="conversation-composer">
      <div className="flex items-end gap-2">
        <textarea value={body} onChange={(event) => setBody(event.target.value)} className={cx(inputCls, "min-h-20 resize-y")} maxLength={4000} placeholder="Write a message…" aria-label="Message" data-testid="conversation-message-input" />
        <Action primary type="submit" disabled={!body.trim() || send.isPending} testId="conversation-send">Send</Action>
      </div>
      <ErrorNote error={send.error} />
    </form>
  );
}

/**
 * The Chief thread's composer. Unlike the ordinary one it posts `expectsReply: true`, which wakes the Chief (or
 * reaches the run in flight). With no thread yet it opens one in the person's current project (spec §5.2).
 */
function ChiefComposer({ chief, threadId, projectId, disabled, onSent }: { chief: ActorDTO; threadId: string | null; projectId: string; disabled: boolean; onSent: (threadId: string) => void }) {
  const ask = useAskChief();
  const [body, setBody] = useState("");
  const blocked = disabled || (!threadId && !projectId);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const text = body.trim();
    if (!text || blocked) return;
    ask.mutate(
      { projectId, to: chief.name, body: text, threadId },
      { onSuccess: (sent) => { setBody(""); onSent(sent.threadId); } },
    );
  };
  return (
    <form onSubmit={submit} className="border-t border-rule/60 pt-4" data-testid="conversation-composer">
      <div className="flex items-end gap-2">
        <textarea
          value={body}
          onChange={(event) => setBody(event.target.value)}
          className={cx(inputCls, "min-h-20 resize-y")}
          maxLength={4000}
          disabled={disabled}
          placeholder={disabled ? "The Chief is not taking messages right now" : `Ask ${chiefLabel(chief)}…`}
          aria-label="Message"
          data-testid="conversation-message-input"
        />
        <Action primary type="submit" disabled={!body.trim() || blocked || ask.isPending} testId="conversation-send">Send</Action>
      </div>
      <ErrorNote error={ask.error} />
    </form>
  );
}

function participantNames(thread: ThreadDTO, actors: ActorDTO[]): string[] {
  return thread.participants.map((id) => historyName(actors, id));
}

function groupThreads(threads: ThreadDTO[]): { taskId: string | null; threads: ThreadDTO[] }[] {
  const groups = new Map<string | null, ThreadDTO[]>();
  for (const thread of threads) groups.set(thread.taskId, [...(groups.get(thread.taskId) ?? []), thread]);
  return [...groups].map(([taskId, items]) => ({ taskId, threads: items }));
}
